// Today view — bento grid orchestrator (CLASSIC theme layout).
// Layout: digest banner → HERO (full-width) → day-status strip → 4 stat tiles → 2-col body.
// B2: PlanReviewBar merged into DayStatusStrip with labeled ProgressBar capacity.
// A3: AttentionDigest banner at top — chips per concern, dismissible per session.
// D35: FocusTimer — device-local countdown, triggered from MIT task cards.
// F7: theme branch lives in bento-shell (only ONE view mounts); all shared
// orchestration (derives, focus timer, modals) comes from useTodayOrchestration.
import type { ReactNode } from "react";
import { AlarmClock, CalendarCheck, CheckCircle2, Flame, Lock, RotateCcw, Sparkles, Sun, Wallet } from "lucide-react";
import { setMonthlyCloseDismissed } from "../lib/app-prefs";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { EntityKind } from "./entity-form";
import { moneyShort, ProgressBar } from "./bento-ui";
import { isAllClear, type DigestState } from "./today-digest-derive";
import type { GettingStartedStep } from "./getting-started-derive";
import { useTodayOrchestration } from "../hooks/use-today-orchestration";
import { TodayHero } from "./today-hero";
import { TodayLeftColumn } from "./today-left-column";
import { TodayRightColumn } from "./today-right-column";
import { MorningPlanningModal } from "./morning-planning-modal";
import { SetupGeneratorModal } from "./setup-generator-modal";
import { aiAvailable } from "../lib/ai/ai-config";
import { EveningShutdownModal } from "./evening-shutdown-modal";
import { WeeklyReviewModal } from "./weekly-review-modal";
import { FocusTimer } from "./focus-timer";
import { PromptDialog } from "./confirm-dialog";
import type { Insight } from "./insights-derive";
import { REPLAN_REASONS } from "./replan-stats-derive";

export function TodayView({
  api,
  onGoto,
  onForm,
}: {
  api: LifeOsApi;
  onGoto: (v: string) => void;
  onForm: (kind: EntityKind, item?: Record<string, unknown>) => void;
}) {
  const {
    d, focusTasks, habitsToday,
    plan, capacity, planned, overCapacity, capacityPercent,
    reviewDue, digest, insights,
    gsSteps, showGettingStarted, handleGsDismiss, handleGsStepClick,
    digestDismissed, dismissDigest,
    showMorning, setShowMorning,
    showEvening, setShowEvening,
    showReview, setShowReview,
    showSetup, setShowSetup,
    showReplanPrompt, setShowReplanPrompt, handleReplan, submitReplan,
    focusTask, handleStartFocus, handleFocusDone, handleFocusStop, handleFocusLogTime,
  } = useTodayOrchestration(api, onForm);

  // MIT ids shown in HERO — exclude from secondary task list to avoid duplication
  const mitIds = new Set(focusTasks.map((t) => t.id));

  // Non-MIT tasks: due today OR in-progress, deduped against MIT set
  const nonMitTasks = [
    ...d.dueToday,
    ...api.state.tasks.filter((t) => t.status === "doing" && t.dueDate !== d.today),
  ]
    .filter((t) => !mitIds.has(t.id))
    .slice(0, 3);

  return (
    <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
      {/* A3: Needs-attention digest banner — full width, session-dismissible.
          Hidden during first-run (getting-started visible): a brand-new user has
          nothing actionable in it yet and Today reads calmer as Plan → Execute. */}
      {!digestDismissed && !showGettingStarted && (
        <div className="col-span-2 lg:col-span-4">
          <AttentionDigest
            digest={digest}
            onDismiss={dismissDigest}
            onGoto={onGoto}
            onOpenMorning={() => setShowMorning(true)}
            onOpenWeeklyReview={() => setShowReview("week")}
            onOpenInbox={() => onGoto("inbox")}
            onOpenMonthlyClose={() => {
              // Bấm chip = chủ động chốt → xoá cờ "Để sau" để Finance tự bật popup, rồi sang Tài chính.
              setMonthlyCloseDismissed("");
              onGoto("finance");
            }}
          />
        </div>
      )}

      {/* AI starter kit — first-run shortcut to populate an empty account fast. */}
      {showGettingStarted && aiAvailable(api) && (
        <button
          onClick={() => setShowSetup(true)}
          className="gbrand col-span-2 flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-[13.5px] font-bold text-white shadow-sm transition hover:opacity-95 lg:col-span-4"
        >
          <Sparkles className="h-4 w-4" />
          Để AI dựng bộ khởi đầu theo công việc của bạn
        </button>
      )}

      {/* A1: Getting-started checklist — shown only when not dismissed and not all done */}
      {showGettingStarted && (
        <div className="col-span-2 lg:col-span-4">
          <GettingStartedCard
            steps={gsSteps}
            onStepClick={handleGsStepClick}
            onDismiss={handleGsDismiss}
          />
        </div>
      )}

      {/* Proactive insights card — only when ≥1 insight exists.
          Positioned after the session-dismissible digest banner so it doesn't
          compete visually; hidden during first-run (no data yet). */}
      {!showGettingStarted && insights.length > 0 && (
        <div className="col-span-2 lg:col-span-4">
          <InsightsCard insights={insights} onGoto={onGoto} />
        </div>
      )}

      {/* HERO — full width */}
      <div className="col-span-2 lg:col-span-4">
        <TodayHero
          api={api}
          focusTasks={focusTasks}
          onGoto={onGoto}
          onMorningPlanning={() => setShowMorning(true)}
          onEveningShutdown={() => setShowEvening(true)}
          onStartFocus={handleStartFocus}
        />
      </div>

      {/* B2: Day-status strip — plan lock + labeled capacity bar + review actions */}
      <div className="col-span-2 lg:col-span-4">
        <DayStatusStrip
          locked={Boolean(plan?.locked)}
          planned={planned}
          capacity={capacity}
          capacityPercent={capacityPercent}
          overCapacity={overCapacity}
          replanReason={plan?.replanReason}
          reviewDue={reviewDue}
          onReplan={handleReplan}
          onWeekly={() => setShowReview("week")}
          onMonthly={() => setShowReview("month")}
        />
      </div>

      {/* 4 stat tiles — full width. Hidden during first-run: all-zero stats add
          noise before the user has data; they appear once getting-started clears.
          Mobile (<lg): hidden — these are at-a-glance overview numbers, secondary to
          the daily-action content above; the same figures live in the digest/hero. */}
      {!showGettingStarted && (
        <>
          {/* Pastel hex không tự đổi theo theme → cần dark: variant tường minh,
              nếu không chữ (ink-900 → sáng trong dark) chìm trên nền pastel sáng. */}
          <div className="hidden lg:block">
            <Stat bg="bg-[#fee2e2] dark:bg-rose-500/15" fg="text-rose-600 dark:text-rose-300" icon={<AlarmClock className="h-5 w-5" />} value={String(d.overdue.length)} label="Quá hạn" />
          </div>
          <div className="hidden lg:block">
            <Stat bg="bg-brand-100" fg="text-brand-600" icon={<Sun className="h-5 w-5" />} value={String(d.dueToday.length)} label="Việc hôm nay" />
          </div>
          <div className="hidden lg:block">
            <Stat bg="bg-[#d1fae5] dark:bg-emerald-500/15" fg="text-emerald-600 dark:text-emerald-300" icon={<Flame className="h-5 w-5" />} value={`${habitsToday}/${d.habitsTotal}`} label="Thói quen" />
          </div>
          <div className="hidden lg:block">
            <Stat bg="bg-[#fef3c7] dark:bg-amber-500/15" fg="text-amber-600 dark:text-amber-300" icon={<Wallet className="h-5 w-5" />} value={moneyShort(d.spentToday)} label="Chi hôm nay" small />
          </div>
        </>
      )}

      {/* 2-column body — left: tasks/calendar/projects, right: inbox/finance/notes */}
      <TodayLeftColumn
        api={api}
        nonMitTasks={nonMitTasks}
        todayEvents={d.todayEvents}
        activeProjects={d.activeProjects}
        onGoto={onGoto}
      />
      <TodayRightColumn
        api={api}
        finance={{ spentToday: d.spentToday, budgetRemaining: d.budgetRemaining, overBudget: d.overBudget }}
        onGoto={onGoto}
        onForm={onForm}
      />

      {/* Modals */}
      {showReplanPrompt && (
        <PromptDialog
          title="Lập lại kế hoạch hôm nay"
          message="Kế hoạch đã khoá — chọn lý do (giúp thống kê), hoặc mở lại luôn."
          placeholder="Lý do khác… (tuỳ chọn)"
          submitLabel="Mở khoá & lập lại"
          allowEmpty
          presets={REPLAN_REASONS}
          onSubmit={submitReplan}
          onClose={() => setShowReplanPrompt(false)}
        />
      )}
      {showSetup && <SetupGeneratorModal api={api} onClose={() => setShowSetup(false)} />}
      {showMorning && <MorningPlanningModal api={api} onClose={() => setShowMorning(false)} />}
      {showEvening && <EveningShutdownModal api={api} onClose={() => setShowEvening(false)} />}
      {showReview && <WeeklyReviewModal api={api} scope={showReview} onClose={() => setShowReview(null)} />}

      {/* D35: Focus timer floating card. key — switching tasks must remount the
          timer (its session lives in a useState initializer bound to the task). */}
      {focusTask && (
        <FocusTimer
          key={focusTask.id}
          task={focusTask}
          onDone={handleFocusDone}
          onStop={handleFocusStop}
          onLogTime={handleFocusLogTime}
        />
      )}
    </div>
  );
}

// ── A1: Getting-started checklist card ───────────────────────────────────────
// Exported: AuraTodayView reuses this block for feature parity (F7).

export function GettingStartedCard({
  steps,
  onStepClick,
  onDismiss,
}: {
  steps: GettingStartedStep[];
  onStepClick: (step: GettingStartedStep) => void;
  onDismiss: () => void;
}) {
  return (
    <div className="getting-started-card fade-in rounded-2xl bg-brand-50 px-4 py-3 ring-1 ring-brand-200/70">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-bold text-brand-700 uppercase tracking-widest">Bắt đầu nào</span>
        <button
          onClick={onDismiss}
          className="text-[11px] text-brand-400 hover:text-brand-600 transition"
          aria-label="Bỏ qua hướng dẫn"
        >
          Bỏ qua
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {steps.map((step) => (
          <button
            key={step.id}
            onClick={() => onStepClick(step)}
            disabled={step.done}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-semibold transition ${
              step.done
                ? "bg-emerald-100 text-emerald-700 cursor-default"
                : "bg-white text-brand-700 ring-1 ring-brand-200 hover:bg-brand-100"
            }`}
          >
            {step.done ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-brand-300" />
            )}
            {step.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── A3: Needs-attention digest banner ─────────────────────────────────────────
// Exported: AuraTodayView reuses this block for feature parity (F7).

export function AttentionDigest({
  digest,
  onDismiss,
  onGoto,
  onOpenMorning,
  onOpenWeeklyReview,
  onOpenInbox,
  onOpenMonthlyClose,
}: {
  digest: DigestState;
  onDismiss: () => void;
  onGoto: (v: string) => void;
  onOpenMorning: () => void;
  onOpenWeeklyReview: () => void;
  onOpenInbox: () => void;
  onOpenMonthlyClose: () => void;
}) {
  if (isAllClear(digest)) {
    return (
      <div className="attention-digest fade-in flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-2.5 text-[13px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span>Mọi thứ đang ổn ✨</span>
      </div>
    );
  }

  return (
    <div className="attention-digest fade-in flex flex-wrap items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2.5 ring-1 ring-amber-200/70 dark:bg-amber-500/10 dark:ring-amber-500/30">
      <span className="text-[12px] font-bold text-amber-700 shrink-0 dark:text-amber-300">Cần chú ý</span>
      <div className="flex flex-wrap gap-1.5 flex-1">
        {digest.overdueCount > 0 && (
          <DigestChip
            color="rose"
            label={`${digest.overdueCount} việc quá hạn`}
            onClick={() => onGoto("tasks")}
          />
        )}
        {digest.overCapacity && (
          <DigestChip
            color="amber"
            label="Vượt sức chứa ngày"
            onClick={onOpenMorning}
          />
        )}
        {digest.reviewDue && (
          <DigestChip
            color="brand"
            label="Review tuần đến hạn"
            onClick={onOpenWeeklyReview}
          />
        )}
        {digest.inboxStale && (
          <DigestChip
            color="ink"
            label="Inbox cần xử lý"
            onClick={() => onGoto("inbox")}
          />
        )}
        {digest.waitingOverdueCount > 0 && (
          <DigestChip
            color="amber"
            label={`⏳ ${digest.waitingOverdueCount} mục chờ đã quá hạn — kiểm tra Inbox`}
            onClick={onOpenInbox}
          />
        )}
        {digest.monthClosePending && (
          <DigestChip
            color="brand"
            label={`📊 Chốt tháng ${digest.monthCloseTarget.slice(5)}/${digest.monthCloseTarget.slice(0, 4)}`}
            onClick={onOpenMonthlyClose}
          />
        )}
        {digest.budgetAlertCount > 0 && (
          <DigestChip
            color="amber"
            label={`⚠️ ${digest.budgetAlertCount} ngân sách sắp chạm hạn mức`}
            onClick={() => onGoto("finance")}
          />
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Bỏ qua thông báo"
        className="shrink-0 text-[11px] text-amber-500 hover:text-amber-700 transition"
      >
        ✕
      </button>
    </div>
  );
}

type ChipColor = "rose" | "amber" | "brand" | "ink";

const chipStyle: Record<ChipColor, string> = {
  rose: "bg-rose-100 text-rose-700 hover:bg-rose-200",
  amber: "bg-amber-100 text-amber-800 hover:bg-amber-200",
  brand: "gbrand text-white hover:opacity-90",
  ink: "bg-ink-100 text-ink-700 hover:bg-ink-200",
};

function DigestChip({ color, label, onClick }: { color: ChipColor; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center rounded-xl px-2.5 py-1 text-[11px] font-bold transition ${chipStyle[color]}`}
    >
      {label}
    </button>
  );
}

// ── B2: Day-status strip (replaces PlanReviewBar) ─────────────────────────────

function DayStatusStrip({
  locked,
  planned,
  capacity,
  capacityPercent,
  overCapacity,
  replanReason,
  reviewDue,
  onReplan,
  onWeekly,
  onMonthly,
}: {
  locked: boolean;
  planned: number;
  capacity: number;
  capacityPercent: number;
  overCapacity: boolean;
  replanReason?: string;
  reviewDue: boolean;
  onReplan: () => void;
  onWeekly: () => void;
  onMonthly: () => void;
}) {
  // F21: semantic class thay cho selector Tailwind mong manh trong bento-theme.css
  return (
    <div className="day-status-strip rounded-3xl bg-white/70 p-3 ring-1 ring-ink-200/60 space-y-2">
      {/* Row 1: lock chip + replan reason + review actions */}
      <div className="flex flex-wrap items-center gap-2">
        {locked ? (
          <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-1.5 text-[12px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
            <Lock className="h-3.5 w-3.5" /> Kế hoạch đã khoá
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-xl bg-ink-100 px-3 py-1.5 text-[12px] font-semibold text-ink-500">
            <Sun className="h-3.5 w-3.5" /> Chưa lập kế hoạch hôm nay
          </span>
        )}
        {replanReason && (
          <span className="text-[11px] italic text-amber-600">Đã lập lại: {replanReason}</span>
        )}
        {locked && (
          <button
            onClick={onReplan}
            className="inline-flex items-center gap-1.5 rounded-xl border border-ink-200 px-3 py-1.5 text-[12px] font-semibold text-ink-600 hover:bg-ink-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Lập lại (khẩn cấp)
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onWeekly}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-bold transition ${
              reviewDue ? "gbrand text-white" : "border border-ink-200 text-ink-600 hover:bg-ink-50"
            }`}
          >
            <CalendarCheck className="h-3.5 w-3.5" />
            Review tuần{reviewDue ? " (đến hạn)" : ""}
          </button>
          <button
            onClick={onMonthly}
            className="inline-flex items-center gap-1.5 rounded-xl border border-ink-200 px-3 py-1.5 text-[12px] font-semibold text-ink-600 hover:bg-ink-50"
          >
            Review tháng
          </button>
        </div>
      </div>

      {/* Row 2: labeled capacity progress bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-ink-500">Sức chứa hôm nay</span>
          <span className={`text-[11px] font-bold ${overCapacity ? "text-rose-600" : "text-ink-500"}`}>
            {planned}′ / {capacity}′{overCapacity ? " · vượt sức chứa" : ""}
          </span>
        </div>
        <ProgressBar percent={capacityPercent} over={overCapacity} />
      </div>
    </div>
  );
}

// ── Insights card — "Điểm cần chú ý" ────────────────────────────────────────
// Calm Bento styling: warn rows = rose/amber accent, info = ink/brand.
// Each row is clickable when the insight has a `view` target.
// Exported: AuraTodayView reuses this block for feature parity (F7).

export function InsightsCard({
  insights,
  onGoto,
}: {
  insights: Insight[];
  onGoto: (v: string) => void;
}) {
  return (
    <div className="insights-card fade-in rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-ink-200/60 space-y-1">
      <p className="text-[12px] font-bold text-ink-500 uppercase tracking-widest mb-2">
        Điểm cần chú ý
      </p>
      {insights.map((insight) => {
        const isWarn = insight.severity === "warn";
        const rowBase =
          "flex items-start gap-2.5 rounded-xl px-3 py-2 text-[13px] transition";
        const rowStyle = isWarn
          ? "bg-rose-50 text-rose-800 ring-1 ring-rose-200/60 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
          : "bg-brand-50 text-ink-700 ring-1 ring-brand-200/40 dark:bg-brand-500/10 dark:text-ink-300";
        const clickable = insight.view
          ? "cursor-pointer hover:brightness-95"
          : "cursor-default";

        return (
          <div
            key={insight.id}
            className={`${rowBase} ${rowStyle} ${clickable}`}
            onClick={insight.view ? () => onGoto(insight.view!) : undefined}
            role={insight.view ? "button" : undefined}
            tabIndex={insight.view ? 0 : undefined}
            onKeyDown={
              insight.view
                ? (e) => { if (e.key === "Enter" || e.key === " ") onGoto(insight.view!); }
                : undefined
            }
          >
            <span className="shrink-0 text-base leading-5">{insight.icon}</span>
            <div className="min-w-0">
              <span className="font-semibold">{insight.title}</span>
              <span className="ml-1.5 text-[12px] opacity-80">{insight.detail}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Stat tile (local, Today-specific) ────────────────────────────────────────

function Stat({ bg, fg, icon, value, label, small }: { bg: string; fg: string; icon: ReactNode; value: string; label: string; small?: boolean }) {
  return (
    <div className={`rounded-3xl ${bg} p-4`}>
      <span className={fg}>{icon}</span>
      <p className={`mt-3 font-display font-bold ${small ? "text-xl" : "text-3xl"}`}>{value}</p>
      <p className={`text-[12px] font-semibold ${fg}`}>{label}</p>
    </div>
  );
}
